import { getDocumentText, searchText, replaceText, isInDocument } from '../../../lib/office';
function isApiSupported(version) {
    return Office.context.requirements.isSetSupported('WordApi', version);
}
function unsupportedApi(feature, requiredVersion) {
    return {
        content: [
            {
                type: 'text',
                text: `${feature} requires WordApi ${requiredVersion}+ which is not available in this environment`,
            },
        ],
    };
}
// ─── Tier 1: Core document operations ────────────────────────────────────────
const wordGetDocumentText = {
    tool: {
        name: 'word_get_document_text',
        description: 'Get the full text content of the current Word document',
        inputSchema: {
            type: 'object',
            properties: {},
        },
    },
    execute: async () => {
        const text = await getDocumentText();
        return { content: [{ type: 'text', text }] };
    },
};
const wordSearchText = {
    tool: {
        name: 'word_search_text',
        description: 'Search for text in the document and highlight the first match',
        inputSchema: {
            type: 'object',
            properties: {
                term: {
                    type: 'string',
                    description: 'The text to search for',
                },
            },
            required: ['term'],
        },
    },
    execute: async (args) => {
        const term = args.term;
        await searchText(term);
        return { content: [{ type: 'text', text: `Searched for: ${term}` }] };
    },
};
const wordReplaceText = {
    tool: {
        name: 'word_replace_text',
        description: 'Replace text in the document. Uses minimal diff to only change the affected portion.',
        inputSchema: {
            type: 'object',
            properties: {
                old_text: {
                    type: 'string',
                    description: 'The text to find and replace',
                },
                new_text: {
                    type: 'string',
                    description: 'The replacement text',
                },
            },
            required: ['old_text', 'new_text'],
        },
    },
    execute: async (args) => {
        const oldText = args.old_text;
        const newText = args.new_text;
        const success = await replaceText(oldText, newText);
        return {
            content: [
                {
                    type: 'text',
                    text: success
                        ? 'Text replaced successfully'
                        : 'Original text not found in document',
                },
            ],
        };
    },
};
const wordIsInDocument = {
    tool: {
        name: 'word_is_in_document',
        description: 'Check if specific text exists in the document',
        inputSchema: {
            type: 'object',
            properties: {
                text: {
                    type: 'string',
                    description: 'The text to search for',
                },
            },
            required: ['text'],
        },
    },
    execute: async (args) => {
        const text = args.text;
        const found = await isInDocument(text);
        return {
            content: [
                {
                    type: 'text',
                    text: found ? 'Text found in document' : 'Text not found in document',
                },
            ],
        };
    },
};
const wordInsertText = {
    tool: {
        name: 'word_insert_text',
        description: 'Insert text into the document at a specified position',
        inputSchema: {
            type: 'object',
            properties: {
                text: {
                    type: 'string',
                    description: 'The text to insert',
                },
                position: {
                    type: 'string',
                    enum: ['start', 'end', 'replace-selection'],
                    description: 'Where to insert the text: "start" (beginning of document), "end" (end of document), or "replace-selection" (replace current selection)',
                },
            },
            required: ['text', 'position'],
        },
    },
    execute: async (args) => {
        const text = args.text;
        const position = args.position;
        await Word.run(async (context) => {
            const body = context.document.body;
            switch (position) {
                case 'start':
                    body.insertText(text, Word.InsertLocation.start);
                    break;
                case 'end':
                    body.insertText(text, Word.InsertLocation.end);
                    break;
                case 'replace-selection': {
                    const selection = context.document.getSelection();
                    selection.insertText(text, Word.InsertLocation.replace);
                    break;
                }
                default:
                    throw new Error(`Invalid position: ${position}`);
            }
            await context.sync();
        });
        return { content: [{ type: 'text', text: `Text inserted at ${position}` }] };
    },
};
const wordGetSelection = {
    tool: {
        name: 'word_get_selection',
        description: 'Get the currently selected text in the document',
        inputSchema: {
            type: 'object',
            properties: {},
        },
    },
    execute: async () => {
        const selectedText = await Word.run(async (context) => {
            const selection = context.document.getSelection();
            context.load(selection, 'text');
            await context.sync();
            return selection.text;
        });
        return { content: [{ type: 'text', text: selectedText || '(no text selected)' }] };
    },
};
// ─── Tier 2: Structural & formatting tools ───────────────────────────────────
const wordGetParagraphs = {
    tool: {
        name: 'word_get_paragraphs',
        description: 'Get all paragraphs in the document with their index, style, and text content',
        inputSchema: {
            type: 'object',
            properties: {},
        },
    },
    execute: async () => {
        const result = await Word.run(async (context) => {
            const paragraphs = context.document.body.paragraphs;
            paragraphs.load('text,style,styleBuiltIn');
            await context.sync();
            return paragraphs.items.map((p, i) => ({
                index: i,
                style: p.style,
                text: p.text,
            }));
        });
        return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
    },
};
const wordInsertParagraph = {
    tool: {
        name: 'word_insert_paragraph',
        description: 'Insert a paragraph into the document',
        inputSchema: {
            type: 'object',
            properties: {
                text: {
                    type: 'string',
                    description: 'The paragraph text',
                },
                style: {
                    type: 'string',
                    enum: [
                        'Normal',
                        'Heading1',
                        'Heading2',
                        'Heading3',
                        'ListBullet',
                        'ListNumber',
                    ],
                    description: 'The paragraph style (default: "Normal")',
                },
                position: {
                    type: 'string',
                    enum: ['start', 'end', 'after-selection'],
                    description: 'Where to insert: "start", "end", or "after-selection" (default: "end")',
                },
            },
            required: ['text'],
        },
    },
    execute: async (args) => {
        const text = args.text;
        const style = args.style ?? 'Normal';
        const position = args.position ?? 'end';
        await Word.run(async (context) => {
            let paragraph;
            if (position === 'after-selection') {
                const selection = context.document.getSelection();
                paragraph = selection.insertParagraph(text, Word.InsertLocation.after);
            }
            else {
                const location = position === 'start' ? Word.InsertLocation.start : Word.InsertLocation.end;
                paragraph = context.document.body.insertParagraph(text, location);
            }
            paragraph.styleBuiltIn = style;
            await context.sync();
        });
        return {
            content: [{ type: 'text', text: `Inserted "${style}" paragraph at ${position}` }],
        };
    },
};
const wordInsertTable = {
    tool: {
        name: 'word_insert_table',
        description: 'Insert a table into the document',
        inputSchema: {
            type: 'object',
            properties: {
                rows: {
                    type: 'array',
                    items: {
                        type: 'array',
                        items: { type: 'string' },
                    },
                    description: 'A 2D array of cell values (rows × columns)',
                },
                position: {
                    type: 'string',
                    enum: ['start', 'end', 'after-selection'],
                    description: 'Where to insert: "start", "end", or "after-selection" (default: "end")',
                },
                has_header: {
                    type: 'boolean',
                    description: 'Whether the first row is a header row with bold formatting (default: true)',
                },
            },
            required: ['rows'],
        },
    },
    execute: async (args) => {
        const rows = args.rows;
        const position = args.position ?? 'end';
        const hasHeader = args.has_header ?? true;
        if (!rows.length || !rows[0].length) {
            return { content: [{ type: 'text', text: 'Error: rows array must not be empty' }] };
        }
        const colCount = rows[0].length;
        const jagged = rows.find((r) => r.length !== colCount);
        if (jagged) {
            return {
                content: [
                    {
                        type: 'text',
                        text: `Error: all rows must have the same number of columns (expected ${colCount}, got ${jagged.length})`,
                    },
                ],
            };
        }
        const rowCount = rows.length;
        await Word.run(async (context) => {
            let table;
            if (position === 'after-selection') {
                const selection = context.document.getSelection();
                table = selection.insertTable(rowCount, colCount, Word.InsertLocation.after, rows);
            }
            else {
                const location = position === 'start' ? Word.InsertLocation.start : Word.InsertLocation.end;
                table = context.document.body.insertTable(rowCount, colCount, location, rows);
            }
            if (hasHeader && rowCount > 0) {
                const headerRow = table.rows.getFirst();
                headerRow.load('cells/items');
                await context.sync();
                for (const cell of headerRow.cells.items) {
                    cell.body.font.bold = true;
                }
            }
            await context.sync();
        });
        return {
            content: [
                {
                    type: 'text',
                    text: `Inserted ${rowCount}×${colCount} table at ${position}`,
                },
            ],
        };
    },
};
const wordFormatText = {
    tool: {
        name: 'word_format_text',
        description: 'Find text in the document and apply formatting to the first match',
        inputSchema: {
            type: 'object',
            properties: {
                search_text: {
                    type: 'string',
                    description: 'The text to find and format',
                },
                bold: {
                    type: 'boolean',
                    description: 'Set bold formatting',
                },
                italic: {
                    type: 'boolean',
                    description: 'Set italic formatting',
                },
                underline: {
                    type: 'boolean',
                    description: 'Set underline formatting',
                },
                color: {
                    type: 'string',
                    description: 'Font color (e.g. "red", "#FF0000")',
                },
                highlight: {
                    type: 'string',
                    description: 'Highlight color (e.g. "yellow", "green")',
                },
            },
            required: ['search_text'],
        },
    },
    execute: async (args) => {
        const searchTextVal = args.search_text;
        const bold = args.bold;
        const italic = args.italic;
        const underline = args.underline;
        const color = args.color;
        const highlight = args.highlight;
        const found = await Word.run(async (context) => {
            const results = context.document.body.search(searchTextVal, {
                matchCase: false,
                matchWholeWord: false,
            });
            results.load('items');
            await context.sync();
            if (results.items.length === 0) {
                return false;
            }
            const range = results.items[0];
            if (bold !== undefined)
                range.font.bold = bold;
            if (italic !== undefined)
                range.font.italic = italic;
            if (underline !== undefined) {
                range.font.underline = underline
                    ? Word.UnderlineType.single
                    : Word.UnderlineType.none;
            }
            if (color)
                range.font.color = color;
            if (highlight)
                range.font.highlightColor = highlight;
            await context.sync();
            return true;
        });
        return {
            content: [
                {
                    type: 'text',
                    text: found
                        ? `Formatted "${searchTextVal}" successfully`
                        : `Text "${searchTextVal}" not found in document`,
                },
            ],
        };
    },
};
// ─── High Priority: Images ───────────────────────────────────────────────────
const wordInsertImage = {
    tool: {
        name: 'word_insert_image',
        description: 'Insert an image into the document from a base64-encoded string',
        inputSchema: {
            type: 'object',
            properties: {
                base64: {
                    type: 'string',
                    description: 'Base64-encoded image data (without the data:image/... prefix)',
                },
                position: {
                    type: 'string',
                    enum: ['start', 'end', 'replace-selection'],
                    description: 'Where to insert: "start", "end", or "replace-selection" (default: "end")',
                },
                width: {
                    type: 'number',
                    description: 'Optional width in points',
                },
                height: {
                    type: 'number',
                    description: 'Optional height in points',
                },
                alt_text: {
                    type: 'string',
                    description: 'Optional alt text for the image',
                },
            },
            required: ['base64'],
        },
    },
    execute: async (args) => {
        const base64 = args.base64;
        const position = args.position ?? 'end';
        const width = args.width;
        const height = args.height;
        const altText = args.alt_text;
        await Word.run(async (context) => {
            let picture;
            if (position === 'replace-selection') {
                const selection = context.document.getSelection();
                picture = selection.insertInlinePictureFromBase64(base64, Word.InsertLocation.replace);
            }
            else {
                const location = position === 'start' ? Word.InsertLocation.start : Word.InsertLocation.end;
                picture = context.document.body.insertInlinePictureFromBase64(base64, location);
            }
            if (width !== undefined)
                picture.width = width;
            if (height !== undefined)
                picture.height = height;
            if (altText !== undefined)
                picture.altTextDescription = altText;
            await context.sync();
        });
        return { content: [{ type: 'text', text: `Image inserted at ${position}` }] };
    },
};
// ─── High Priority: Content Controls ─────────────────────────────────────────
const wordGetContentControls = {
    tool: {
        name: 'word_get_content_controls',
        description: 'Get all content controls in the document with their tag, title, type, and text',
        inputSchema: {
            type: 'object',
            properties: {
                tag: {
                    type: 'string',
                    description: 'Optional tag to filter content controls by',
                },
            },
        },
    },
    execute: async (args) => {
        const tag = args.tag;
        const result = await Word.run(async (context) => {
            let controls;
            if (tag) {
                controls = context.document.contentControls.getByTag(tag);
            }
            else {
                controls = context.document.contentControls;
            }
            controls.load('items/tag,items/title,items/type,items/text,items/id');
            await context.sync();
            return controls.items.map((cc) => ({
                id: cc.id,
                tag: cc.tag,
                title: cc.title,
                type: cc.type,
                text: cc.text,
            }));
        });
        return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
    },
};
const wordInsertContentControl = {
    tool: {
        name: 'word_insert_content_control',
        description: 'Wrap the current selection in a content control, or insert a new one at the end of the document',
        inputSchema: {
            type: 'object',
            properties: {
                tag: {
                    type: 'string',
                    description: 'A tag to identify the content control',
                },
                title: {
                    type: 'string',
                    description: 'A visible title for the content control',
                },
                text: {
                    type: 'string',
                    description: 'Text to place inside the content control. If omitted and there is a selection, the selection is wrapped.',
                },
                appearance: {
                    type: 'string',
                    enum: ['BoundingBox', 'Tags', 'Hidden'],
                    description: 'Visual appearance of the content control (default: "BoundingBox")',
                },
            },
        },
    },
    execute: async (args) => {
        const tag = args.tag;
        const title = args.title;
        const text = args.text;
        const appearance = args.appearance;
        const resultId = await Word.run(async (context) => {
            let range;
            if (text !== undefined) {
                const paragraph = context.document.body.insertParagraph(text, Word.InsertLocation.end);
                range = paragraph.getRange();
            }
            else {
                range = context.document.getSelection();
            }
            const cc = range.insertContentControl();
            if (tag)
                cc.tag = tag;
            if (title)
                cc.title = title;
            if (appearance)
                cc.appearance = appearance;
            cc.load('id');
            await context.sync();
            return cc.id;
        });
        return {
            content: [{ type: 'text', text: `Content control created with id: ${resultId}` }],
        };
    },
};
const wordSetContentControl = {
    tool: {
        name: 'word_set_content_control',
        description: 'Set the text or properties of an existing content control by tag or id',
        inputSchema: {
            type: 'object',
            properties: {
                tag: {
                    type: 'string',
                    description: 'Tag of the content control to update (uses first match)',
                },
                id: {
                    type: 'number',
                    description: 'ID of the content control to update (takes precedence over tag)',
                },
                text: {
                    type: 'string',
                    description: 'New text content',
                },
                new_tag: {
                    type: 'string',
                    description: 'New tag value',
                },
                new_title: {
                    type: 'string',
                    description: 'New title value',
                },
            },
        },
    },
    execute: async (args) => {
        const tag = args.tag;
        const id = args.id;
        const text = args.text;
        const newTag = args.new_tag;
        const newTitle = args.new_title;
        if (!tag && id === undefined) {
            return {
                content: [{ type: 'text', text: 'Error: either "tag" or "id" is required' }],
            };
        }
        await Word.run(async (context) => {
            let cc;
            if (id !== undefined) {
                cc = context.document.contentControls.getById(id);
            }
            else {
                const controls = context.document.contentControls.getByTag(tag);
                controls.load('items');
                await context.sync();
                if (controls.items.length === 0) {
                    throw new Error(`No content control found with tag "${tag}"`);
                }
                cc = controls.items[0];
            }
            if (text !== undefined)
                cc.insertText(text, Word.InsertLocation.replace);
            if (newTag)
                cc.tag = newTag;
            if (newTitle)
                cc.title = newTitle;
            await context.sync();
        });
        return { content: [{ type: 'text', text: 'Content control updated' }] };
    },
};
const wordDeleteContentControl = {
    tool: {
        name: 'word_delete_content_control',
        description: 'Delete a content control by tag or id. Can keep or remove the contained text.',
        inputSchema: {
            type: 'object',
            properties: {
                tag: {
                    type: 'string',
                    description: 'Tag of the content control to delete (uses first match)',
                },
                id: {
                    type: 'number',
                    description: 'ID of the content control to delete (takes precedence over tag)',
                },
                keep_content: {
                    type: 'boolean',
                    description: 'If true, keeps the text but removes the control wrapper. If false, deletes everything. (default: true)',
                },
            },
        },
    },
    execute: async (args) => {
        const tag = args.tag;
        const id = args.id;
        const keepContent = args.keep_content ?? true;
        if (!tag && id === undefined) {
            return {
                content: [{ type: 'text', text: 'Error: either "tag" or "id" is required' }],
            };
        }
        await Word.run(async (context) => {
            let cc;
            if (id !== undefined) {
                cc = context.document.contentControls.getById(id);
            }
            else {
                const controls = context.document.contentControls.getByTag(tag);
                controls.load('items');
                await context.sync();
                if (controls.items.length === 0) {
                    throw new Error(`No content control found with tag "${tag}"`);
                }
                cc = controls.items[0];
            }
            if (keepContent) {
                cc.delete(true);
            }
            else {
                cc.delete(false);
            }
            await context.sync();
        });
        return {
            content: [
                {
                    type: 'text',
                    text: keepContent
                        ? 'Content control removed (content kept)'
                        : 'Content control and its content deleted',
                },
            ],
        };
    },
};
// ─── High Priority: Hyperlinks ───────────────────────────────────────────────
const wordInsertHyperlink = {
    tool: {
        name: 'word_insert_hyperlink',
        description: 'Find text in the document and turn it into a hyperlink, or insert a new hyperlink',
        inputSchema: {
            type: 'object',
            properties: {
                url: {
                    type: 'string',
                    description: 'The URL for the hyperlink',
                },
                text: {
                    type: 'string',
                    description: 'The display text. If it matches existing document text, that text becomes the link. Otherwise, text is inserted at the end.',
                },
            },
            required: ['url', 'text'],
        },
    },
    execute: async (args) => {
        const url = args.url;
        const text = args.text;
        const allowedSchemes = /^https?:\/\/|^mailto:/i;
        if (!allowedSchemes.test(url)) {
            return {
                content: [
                    {
                        type: 'text',
                        text: `Error: URL must use http:, https:, or mailto: scheme. Got: "${url}"`,
                    },
                ],
            };
        }
        await Word.run(async (context) => {
            const results = context.document.body.search(text, {
                matchCase: false,
                matchWholeWord: true,
            });
            results.load('items');
            await context.sync();
            let range;
            if (results.items.length > 0) {
                range = results.items[0];
            }
            else {
                const paragraph = context.document.body.insertParagraph('', Word.InsertLocation.end);
                range = paragraph.insertText(text, Word.InsertLocation.start);
            }
            range.hyperlink = url;
            await context.sync();
        });
        return { content: [{ type: 'text', text: `Hyperlink set on "${text}" → ${url}` }] };
    },
};
// ─── High Priority: Document Properties ──────────────────────────────────────
const wordGetDocumentProperties = {
    tool: {
        name: 'word_get_document_properties',
        description: 'Get document properties (title, author, subject, keywords, etc.)',
        inputSchema: {
            type: 'object',
            properties: {},
        },
    },
    execute: async () => {
        const result = await Word.run(async (context) => {
            const props = context.document.properties;
            props.load('title,author,subject,keywords,comments,category,lastAuthor,creationDate,lastSaveTime,revisionNumber');
            await context.sync();
            return {
                title: props.title,
                author: props.author,
                subject: props.subject,
                keywords: props.keywords,
                comments: props.comments,
                category: props.category,
                lastAuthor: props.lastAuthor,
                creationDate: props.creationDate,
                lastSaveTime: props.lastSaveTime,
                revisionNumber: props.revisionNumber,
            };
        });
        return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
    },
};
const wordSetDocumentProperties = {
    tool: {
        name: 'word_set_document_properties',
        description: 'Set document properties (title, author, subject, keywords, etc.)',
        inputSchema: {
            type: 'object',
            properties: {
                title: { type: 'string', description: 'Document title' },
                author: { type: 'string', description: 'Document author' },
                subject: { type: 'string', description: 'Document subject' },
                keywords: { type: 'string', description: 'Document keywords' },
                comments: { type: 'string', description: 'Document comments' },
                category: { type: 'string', description: 'Document category' },
            },
        },
    },
    execute: async (args) => {
        const updates = [];
        await Word.run(async (context) => {
            const props = context.document.properties;
            if (args.title !== undefined) {
                props.title = args.title;
                updates.push('title');
            }
            if (args.author !== undefined) {
                props.author = args.author;
                updates.push('author');
            }
            if (args.subject !== undefined) {
                props.subject = args.subject;
                updates.push('subject');
            }
            if (args.keywords !== undefined) {
                props.keywords = args.keywords;
                updates.push('keywords');
            }
            if (args.comments !== undefined) {
                props.comments = args.comments;
                updates.push('comments');
            }
            if (args.category !== undefined) {
                props.category = args.category;
                updates.push('category');
            }
            await context.sync();
        });
        return {
            content: [
                {
                    type: 'text',
                    text: updates.length
                        ? `Updated properties: ${updates.join(', ')}`
                        : 'No properties specified to update',
                },
            ],
        };
    },
};
// ─── High Priority: Comments (WordApi 1.4+) ──────────────────────────────────
// Helper to get the comment collection from the document body.
// The Comments API surface varies across Word JS API versions and platforms.
// We try body.getComments() first, then fall back to body.getRange().getComments().
async function getCommentCollection(context) {
    const body = context.document.body;
    if (typeof body.getComments === 'function') {
        return body.getComments();
    }
    const range = body.getRange();
    if (typeof range.getComments === 'function') {
        return range.getComments();
    }
    throw new Error('Comments API is not available in this environment. Neither Body.getComments() nor Range.getComments() is supported.');
}
const wordGetComments = {
    tool: {
        name: 'word_get_comments',
        description: 'Get all comments in the document (requires WordApi 1.4+)',
        inputSchema: {
            type: 'object',
            properties: {},
        },
    },
    execute: async () => {
        if (!isApiSupported('1.4'))
            return unsupportedApi('Comments', '1.4');
        const result = await Word.run(async (context) => {
            const comments = await getCommentCollection(context);
            comments.load('items/id,items/content,items/authorName,items/creationDate');
            await context.sync();
            return comments.items.map((c) => ({
                id: c.id,
                content: c.content,
                author: c.authorName,
                creationDate: c.creationDate,
            }));
        });
        return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
    },
};
const wordInsertComment = {
    tool: {
        name: 'word_insert_comment',
        description: 'Add a comment to text found in the document (requires WordApi 1.4+)',
        inputSchema: {
            type: 'object',
            properties: {
                search_text: {
                    type: 'string',
                    description: 'The text to attach the comment to (first match)',
                },
                comment: {
                    type: 'string',
                    description: 'The comment text',
                },
            },
            required: ['search_text', 'comment'],
        },
    },
    execute: async (args) => {
        if (!isApiSupported('1.4'))
            return unsupportedApi('Comments', '1.4');
        const searchTextVal = args.search_text;
        const commentText = args.comment;
        await Word.run(async (context) => {
            const results = context.document.body.search(searchTextVal, {
                matchCase: false,
                matchWholeWord: false,
            });
            results.load('items');
            await context.sync();
            if (results.items.length === 0) {
                throw new Error(`Text "${searchTextVal}" not found in document`);
            }
            const range = results.items[0];
            if (typeof range.insertComment !== 'function') {
                throw new Error('Range.insertComment() is not available in this environment');
            }
            const comment = range.insertComment(commentText);
            comment.load('id');
            await context.sync();
        });
        return {
            content: [{ type: 'text', text: `Comment added to "${searchTextVal}"` }],
        };
    },
};
const wordDeleteComment = {
    tool: {
        name: 'word_delete_comment',
        description: 'Delete a comment by its ID (requires WordApi 1.4+)',
        inputSchema: {
            type: 'object',
            properties: {
                id: {
                    type: 'number',
                    description: 'The ID of the comment to delete (from word_get_comments)',
                },
            },
            required: ['id'],
        },
    },
    execute: async (args) => {
        if (!isApiSupported('1.4'))
            return unsupportedApi('Comments', '1.4');
        const commentId = args.id;
        await Word.run(async (context) => {
            const comments = await getCommentCollection(context);
            comments.load('items/id');
            await context.sync();
            const target = comments.items.find((c) => c.id === commentId);
            if (!target) {
                throw new Error(`Comment with id ${commentId} not found`);
            }
            target.delete();
            await context.sync();
        });
        return { content: [{ type: 'text', text: `Comment ${commentId} deleted` }] };
    },
};
// ─── High Priority: Headers & Footers ────────────────────────────────────────
const wordGetHeadersFooters = {
    tool: {
        name: 'word_get_headers_footers',
        description: 'Get the text content of headers and footers for all sections in the document',
        inputSchema: {
            type: 'object',
            properties: {
                section_index: {
                    type: 'number',
                    description: 'Optional 0-based section index. If omitted, returns all sections.',
                },
            },
        },
    },
    execute: async (args) => {
        const sectionIndex = args.section_index;
        const result = await Word.run(async (context) => {
            const sections = context.document.sections;
            sections.load('items');
            await context.sync();
            if (sectionIndex !== undefined && (sectionIndex < 0 || !sections.items[sectionIndex])) {
                throw new Error(`Section ${sectionIndex} not found. Document has ${sections.items.length} section(s).`);
            }
            const sectionItems = sectionIndex !== undefined ? [sections.items[sectionIndex]] : sections.items;
            const results = [];
            for (let i = 0; i < sectionItems.length; i++) {
                const section = sectionItems[i];
                const headerPrimary = section.getHeader('Primary');
                const footerPrimary = section.getFooter('Primary');
                const headerFirstPage = section.getHeader('FirstPage');
                const footerFirstPage = section.getFooter('FirstPage');
                const headerEvenPages = section.getHeader('EvenPages');
                const footerEvenPages = section.getFooter('EvenPages');
                context.load(headerPrimary, 'text');
                context.load(footerPrimary, 'text');
                context.load(headerFirstPage, 'text');
                context.load(footerFirstPage, 'text');
                context.load(headerEvenPages, 'text');
                context.load(footerEvenPages, 'text');
                await context.sync();
                results.push({
                    section: sectionIndex !== undefined ? sectionIndex : i,
                    headers: {
                        primary: headerPrimary.text,
                        firstPage: headerFirstPage.text,
                        evenPages: headerEvenPages.text,
                    },
                    footers: {
                        primary: footerPrimary.text,
                        firstPage: footerFirstPage.text,
                        evenPages: footerEvenPages.text,
                    },
                });
            }
            return results;
        });
        return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
    },
};
const wordSetHeaderFooter = {
    tool: {
        name: 'word_set_header_footer',
        description: 'Set the text content of a header or footer in a document section',
        inputSchema: {
            type: 'object',
            properties: {
                type: {
                    type: 'string',
                    enum: ['header', 'footer'],
                    description: 'Whether to set a header or footer',
                },
                text: {
                    type: 'string',
                    description: 'The text content',
                },
                section_index: {
                    type: 'number',
                    description: '0-based section index (default: 0)',
                },
                page_type: {
                    type: 'string',
                    enum: ['Primary', 'FirstPage', 'EvenPages'],
                    description: 'Which page type to set (default: "Primary")',
                },
            },
            required: ['type', 'text'],
        },
    },
    execute: async (args) => {
        const type = args.type;
        const text = args.text;
        const sectionIndex = args.section_index ?? 0;
        const pageType = args.page_type ?? 'Primary';
        await Word.run(async (context) => {
            const sections = context.document.sections;
            sections.load('items');
            await context.sync();
            if (!sections.items[sectionIndex]) {
                throw new Error(`Section ${sectionIndex} not found. Document has ${sections.items.length} section(s).`);
            }
            const section = sections.items[sectionIndex];
            const body = type === 'header'
                ? section.getHeader(pageType)
                : section.getFooter(pageType);
            body.clear();
            body.insertText(text, Word.InsertLocation.start);
            await context.sync();
        });
        return {
            content: [
                {
                    type: 'text',
                    text: `${type === 'header' ? 'Header' : 'Footer'} (${pageType}) set for section ${sectionIndex}`,
                },
            ],
        };
    },
};
// ─── Medium Priority: Sections ───────────────────────────────────────────────
const wordGetSections = {
    tool: {
        name: 'word_get_sections',
        description: 'Get section information including count and page setup details',
        inputSchema: {
            type: 'object',
            properties: {},
        },
    },
    execute: async () => {
        const result = await Word.run(async (context) => {
            const sections = context.document.sections;
            sections.load('items');
            await context.sync();
            const bodies = sections.items.map((s) => s.body);
            for (const body of bodies) {
                body.load('text');
            }
            await context.sync();
            const sectionData = bodies.map((body, i) => ({
                index: i,
                textLength: body.text.length,
                textPreview: body.text.substring(0, 200) + (body.text.length > 200 ? '...' : ''),
            }));
            return { count: sections.items.length, sections: sectionData };
        });
        return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
    },
};
// ─── Medium Priority: Breaks ─────────────────────────────────────────────────
const wordInsertBreak = {
    tool: {
        name: 'word_insert_break',
        description: 'Insert a page break, section break, or line break into the document',
        inputSchema: {
            type: 'object',
            properties: {
                break_type: {
                    type: 'string',
                    enum: [
                        'Page',
                        'Next',
                        'SectionNext',
                        'SectionContinuous',
                        'SectionEven',
                        'SectionOdd',
                        'Line',
                    ],
                    description: 'The type of break: "Page" (page break), "Next"/"SectionNext" (section break next page), "SectionContinuous", "SectionEven", "SectionOdd", "Line" (line break)',
                },
                position: {
                    type: 'string',
                    enum: ['end', 'after-selection'],
                    description: 'Where to insert: "end" or "after-selection" (default: "end")',
                },
            },
            required: ['break_type'],
        },
    },
    execute: async (args) => {
        const breakType = args.break_type;
        const position = args.position ?? 'end';
        const breakTypeMap = {
            Page: Word.BreakType.page,
            Next: Word.BreakType.next,
            SectionNext: Word.BreakType.sectionNext,
            SectionContinuous: Word.BreakType.sectionContinuous,
            SectionEven: Word.BreakType.sectionEven,
            SectionOdd: Word.BreakType.sectionOdd,
            Line: Word.BreakType.line,
        };
        const wordBreakType = breakTypeMap[breakType];
        if (!wordBreakType && wordBreakType !== 0) {
            return { content: [{ type: 'text', text: `Invalid break type: ${breakType}` }] };
        }
        await Word.run(async (context) => {
            if (position === 'after-selection') {
                const selection = context.document.getSelection();
                selection.insertBreak(wordBreakType, Word.InsertLocation.after);
            }
            else {
                context.document.body.insertBreak(wordBreakType, Word.InsertLocation.end);
            }
            await context.sync();
        });
        return { content: [{ type: 'text', text: `${breakType} break inserted at ${position}` }] };
    },
};
// ─── Medium Priority: Lists ──────────────────────────────────────────────────
const wordGetListInfo = {
    tool: {
        name: 'word_get_list_info',
        description: 'Get list information for paragraphs in the document (which paragraphs are list items, their level, etc.)',
        inputSchema: {
            type: 'object',
            properties: {},
        },
    },
    execute: async () => {
        const result = await Word.run(async (context) => {
            const paragraphs = context.document.body.paragraphs;
            paragraphs.load('items/text,items/style,items/styleBuiltIn');
            await context.sync();
            const listItemRefs = paragraphs.items.map((p) => p.listItemOrNullObject);
            for (const li of listItemRefs) {
                li.load('level,listString,siblingIndex');
            }
            await context.sync();
            const listItems = [];
            for (let i = 0; i < paragraphs.items.length; i++) {
                const li = listItemRefs[i];
                if (!li.isNullObject) {
                    listItems.push({
                        paragraphIndex: i,
                        text: paragraphs.items[i].text,
                        level: li.level,
                        listString: li.listString,
                        siblingIndex: li.siblingIndex,
                    });
                }
            }
            return listItems;
        });
        return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
    },
};
const wordSetListProperties = {
    tool: {
        name: 'word_set_list_properties',
        description: 'Set list properties on a paragraph (indent level) by paragraph index',
        inputSchema: {
            type: 'object',
            properties: {
                paragraph_index: {
                    type: 'number',
                    description: 'The 0-based paragraph index',
                },
                level: {
                    type: 'number',
                    description: 'The list indent level (0-8)',
                },
            },
            required: ['paragraph_index', 'level'],
        },
    },
    execute: async (args) => {
        const paragraphIndex = args.paragraph_index;
        const level = args.level;
        await Word.run(async (context) => {
            const paragraphs = context.document.body.paragraphs;
            paragraphs.load('items');
            await context.sync();
            if (paragraphIndex < 0 || paragraphIndex >= paragraphs.items.length) {
                throw new Error(`Paragraph index ${paragraphIndex} out of range (${paragraphs.items.length} paragraphs)`);
            }
            const p = paragraphs.items[paragraphIndex];
            const listItem = p.listItemOrNullObject;
            listItem.load();
            await context.sync();
            if (listItem.isNullObject) {
                throw new Error(`Paragraph ${paragraphIndex} is not a list item. Use word_insert_paragraph with ListBullet or ListNumber style first.`);
            }
            listItem.level = level;
            await context.sync();
        });
        return {
            content: [
                {
                    type: 'text',
                    text: `Paragraph ${paragraphIndex} list level set to ${level}`,
                },
            ],
        };
    },
};
// ─── Medium Priority: OOXML ──────────────────────────────────────────────────
const wordInsertOoxml = {
    tool: {
        name: 'word_insert_ooxml',
        description: 'Insert Office Open XML content into the document for rich formatting not achievable through other tools. Warning: OOXML can contain macros or external references — only insert trusted content.',
        inputSchema: {
            type: 'object',
            properties: {
                ooxml: {
                    type: 'string',
                    description: 'The OOXML string to insert',
                },
                position: {
                    type: 'string',
                    enum: ['start', 'end', 'replace-selection'],
                    description: 'Where to insert (default: "end")',
                },
            },
            required: ['ooxml'],
        },
    },
    execute: async (args) => {
        const ooxml = args.ooxml;
        const position = args.position ?? 'end';
        await Word.run(async (context) => {
            if (position === 'replace-selection') {
                const selection = context.document.getSelection();
                selection.insertOoxml(ooxml, Word.InsertLocation.replace);
            }
            else {
                const location = position === 'start' ? Word.InsertLocation.start : Word.InsertLocation.end;
                context.document.body.insertOoxml(ooxml, location);
            }
            await context.sync();
        });
        return { content: [{ type: 'text', text: `OOXML content inserted at ${position}` }] };
    },
};
const wordGetOoxml = {
    tool: {
        name: 'word_get_ooxml',
        description: 'Get the OOXML representation of the current selection or the full document body',
        inputSchema: {
            type: 'object',
            properties: {
                source: {
                    type: 'string',
                    enum: ['selection', 'body'],
                    description: 'What to get OOXML for (default: "selection")',
                },
            },
        },
    },
    execute: async (args) => {
        const source = args.source ?? 'selection';
        const ooxml = await Word.run(async (context) => {
            const range = source === 'body'
                ? context.document.body.getRange()
                : context.document.getSelection();
            const result = range.getOoxml();
            await context.sync();
            return result.value;
        });
        return { content: [{ type: 'text', text: ooxml }] };
    },
};
// ─── Medium Priority: Footnotes (WordApi 1.5+) ──────────────────────────────
const wordInsertFootnote = {
    tool: {
        name: 'word_insert_footnote',
        description: 'Insert a footnote or endnote at the first occurrence of specified text (requires WordApi 1.5+)',
        inputSchema: {
            type: 'object',
            properties: {
                search_text: {
                    type: 'string',
                    description: 'The text to attach the footnote to (first match)',
                },
                footnote_text: {
                    type: 'string',
                    description: 'The footnote content text',
                },
                type: {
                    type: 'string',
                    enum: ['Footnote', 'Endnote'],
                    description: 'Whether to insert a footnote or endnote (default: "Footnote")',
                },
            },
            required: ['search_text', 'footnote_text'],
        },
    },
    execute: async (args) => {
        if (!isApiSupported('1.5'))
            return unsupportedApi('Footnotes', '1.5');
        const searchTextVal = args.search_text;
        const footnoteText = args.footnote_text;
        const noteType = args.type ?? 'Footnote';
        await Word.run(async (context) => {
            const results = context.document.body.search(searchTextVal, {
                matchCase: false,
                matchWholeWord: false,
            });
            results.load('items');
            await context.sync();
            if (results.items.length === 0) {
                throw new Error(`Text "${searchTextVal}" not found in document`);
            }
            const range = results.items[0];
            if (noteType === 'Endnote') {
                ;
                range.insertEndnote(footnoteText);
            }
            else {
                ;
                range.insertFootnote(footnoteText);
            }
            await context.sync();
        });
        return {
            content: [{ type: 'text', text: `${noteType} added at "${searchTextVal}"` }],
        };
    },
};
const wordGetFootnotes = {
    tool: {
        name: 'word_get_footnotes',
        description: 'Get all footnotes and endnotes in the document (requires WordApi 1.5+)',
        inputSchema: {
            type: 'object',
            properties: {
                type: {
                    type: 'string',
                    enum: ['Footnote', 'Endnote', 'Both'],
                    description: 'Which type to retrieve (default: "Both")',
                },
            },
        },
    },
    execute: async (args) => {
        if (!isApiSupported('1.5'))
            return unsupportedApi('Footnotes', '1.5');
        const noteType = args.type ?? 'Both';
        const result = await Word.run(async (context) => {
            const body = context.document.body;
            const notes = [];
            if (noteType === 'Footnote' || noteType === 'Both') {
                const footnotes = body.footnotes;
                footnotes.load('items/body/text');
                await context.sync();
                for (const fn of footnotes.items) {
                    notes.push({ type: 'Footnote', text: fn.body.text });
                }
            }
            if (noteType === 'Endnote' || noteType === 'Both') {
                const endnotes = body.endnotes;
                endnotes.load('items/body/text');
                await context.sync();
                for (const en of endnotes.items) {
                    notes.push({ type: 'Endnote', text: en.body.text });
                }
            }
            return notes;
        });
        return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
    },
};
// ─── Medium Priority: Styles (WordApi 1.5+) ─────────────────────────────────
const wordGetStyles = {
    tool: {
        name: 'word_get_styles',
        description: 'Get available styles in the document (requires WordApi 1.5+)',
        inputSchema: {
            type: 'object',
            properties: {
                type: {
                    type: 'string',
                    enum: ['Character', 'Paragraph', 'Both'],
                    description: 'Filter by style type (default: "Both")',
                },
            },
        },
    },
    execute: async (args) => {
        if (!isApiSupported('1.5'))
            return unsupportedApi('Styles', '1.5');
        const styleType = args.type ?? 'Both';
        const result = await Word.run(async (context) => {
            const styles = context.document.getStyles();
            styles.load('items/nameLocal,items/type,items/builtIn');
            await context.sync();
            let items = styles.items.map((s) => ({
                name: s.nameLocal,
                type: s.type,
                builtIn: s.builtIn,
            }));
            if (styleType !== 'Both') {
                items = items.filter((s) => s.type === styleType);
            }
            return items;
        });
        return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
    },
};
// ─── Lower Priority: Bookmarks ───────────────────────────────────────────────
const wordGetBookmarks = {
    tool: {
        name: 'word_get_bookmarks',
        description: 'Get all bookmark names in the document (requires WordApi 1.4+)',
        inputSchema: {
            type: 'object',
            properties: {},
        },
    },
    execute: async () => {
        if (!isApiSupported('1.4'))
            return unsupportedApi('Bookmarks', '1.4');
        const result = await Word.run(async (context) => {
            const body = context.document.body;
            const bookmarks = body.getBookmarks(true);
            await context.sync();
            return bookmarks.value;
        });
        return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
    },
};
const wordInsertBookmark = {
    tool: {
        name: 'word_insert_bookmark',
        description: 'Insert a bookmark at the first occurrence of specified text or at the current selection (requires WordApi 1.4+)',
        inputSchema: {
            type: 'object',
            properties: {
                name: {
                    type: 'string',
                    description: 'The bookmark name (must be unique)',
                },
                search_text: {
                    type: 'string',
                    description: 'Text to place the bookmark at (first match). If omitted, bookmarks the current selection.',
                },
            },
            required: ['name'],
        },
    },
    execute: async (args) => {
        if (!isApiSupported('1.4'))
            return unsupportedApi('Bookmarks', '1.4');
        const name = args.name;
        const searchTextVal = args.search_text;
        await Word.run(async (context) => {
            let range;
            if (searchTextVal) {
                const results = context.document.body.search(searchTextVal, {
                    matchCase: false,
                    matchWholeWord: false,
                });
                results.load('items');
                await context.sync();
                if (results.items.length === 0) {
                    throw new Error(`Text "${searchTextVal}" not found in document`);
                }
                range = results.items[0];
            }
            else {
                range = context.document.getSelection();
            }
            ;
            range.insertBookmark(name);
            await context.sync();
        });
        return { content: [{ type: 'text', text: `Bookmark "${name}" inserted` }] };
    },
};
const wordDeleteBookmark = {
    tool: {
        name: 'word_delete_bookmark',
        description: 'Delete a bookmark by name (requires WordApi 1.4+)',
        inputSchema: {
            type: 'object',
            properties: {
                name: {
                    type: 'string',
                    description: 'The bookmark name to delete',
                },
            },
            required: ['name'],
        },
    },
    execute: async (args) => {
        if (!isApiSupported('1.4'))
            return unsupportedApi('Bookmarks', '1.4');
        const name = args.name;
        await Word.run(async (context) => {
            const range = context.document.getBookmarkRange(name);
            range.deleteBookmark(name);
            await context.sync();
        });
        return { content: [{ type: 'text', text: `Bookmark "${name}" deleted` }] };
    },
};
// ─── Lower Priority: Tracked Changes ────────────────────────────────────────
const wordGetTrackedChanges = {
    tool: {
        name: 'word_get_tracked_changes',
        description: 'Get all tracked changes in the document with their type and text (requires WordApi 1.6+)',
        inputSchema: {
            type: 'object',
            properties: {},
        },
    },
    execute: async () => {
        if (!isApiSupported('1.6'))
            return unsupportedApi('Tracked changes', '1.6');
        const result = await Word.run(async (context) => {
            const trackedChanges = context.document.body.getTrackedChanges();
            trackedChanges.load('items');
            await context.sync();
            const ranges = trackedChanges.items.map((change) => {
                context.load(change, 'type,author,date');
                const range = change.getRange();
                range.load('text');
                return range;
            });
            await context.sync();
            return trackedChanges.items.map((change, i) => ({
                type: change.type,
                author: change.author,
                date: change.date,
                text: ranges[i].text,
            }));
        });
        return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
    },
};
const wordAcceptTrackedChange = {
    tool: {
        name: 'word_accept_tracked_change',
        description: 'Accept tracked changes in the document. Can accept all or a specific change by index. (requires WordApi 1.6+)',
        inputSchema: {
            type: 'object',
            properties: {
                index: {
                    type: 'number',
                    description: 'The 0-based index of the tracked change to accept (from word_get_tracked_changes). If omitted, accepts all tracked changes.',
                },
            },
        },
    },
    execute: async (args) => {
        if (!isApiSupported('1.6'))
            return unsupportedApi('Accept tracked changes', '1.6');
        const index = args.index;
        const count = await Word.run(async (context) => {
            const trackedChanges = context.document.body.getTrackedChanges();
            trackedChanges.load('items');
            await context.sync();
            if (index !== undefined) {
                if (index >= trackedChanges.items.length) {
                    throw new Error(`Index ${index} out of range (${trackedChanges.items.length} tracked changes)`);
                }
                ;
                trackedChanges.items[index].accept();
                await context.sync();
                return 1;
            }
            for (const change of trackedChanges.items) {
                ;
                change.accept();
            }
            await context.sync();
            return trackedChanges.items.length;
        });
        return { content: [{ type: 'text', text: `Accepted ${count} tracked change(s)` }] };
    },
};
const wordRejectTrackedChange = {
    tool: {
        name: 'word_reject_tracked_change',
        description: 'Reject tracked changes in the document. Can reject all or a specific change by index. (requires WordApi 1.6+)',
        inputSchema: {
            type: 'object',
            properties: {
                index: {
                    type: 'number',
                    description: 'The 0-based index of the tracked change to reject (from word_get_tracked_changes). If omitted, rejects all tracked changes.',
                },
            },
        },
    },
    execute: async (args) => {
        if (!isApiSupported('1.6'))
            return unsupportedApi('Reject tracked changes', '1.6');
        const index = args.index;
        const count = await Word.run(async (context) => {
            const trackedChanges = context.document.body.getTrackedChanges();
            trackedChanges.load('items');
            await context.sync();
            if (index !== undefined) {
                if (index >= trackedChanges.items.length) {
                    throw new Error(`Index ${index} out of range (${trackedChanges.items.length} tracked changes)`);
                }
                ;
                trackedChanges.items[index].reject();
                await context.sync();
                return 1;
            }
            for (const change of trackedChanges.items) {
                ;
                change.reject();
            }
            await context.sync();
            return trackedChanges.items.length;
        });
        return { content: [{ type: 'text', text: `Rejected ${count} tracked change(s)` }] };
    },
};
// ─── Lower Priority: Save Document ──────────────────────────────────────────
const wordSaveDocument = {
    tool: {
        name: 'word_save_document',
        description: 'Save the current document',
        inputSchema: {
            type: 'object',
            properties: {},
        },
    },
    execute: async () => {
        await Word.run(async (context) => {
            context.document.save();
            await context.sync();
        });
        return { content: [{ type: 'text', text: 'Document saved' }] };
    },
};
// ─── Lower Priority: Fields (WordApi 1.5+) ──────────────────────────────────
const wordInsertField = {
    tool: {
        name: 'word_insert_field',
        description: 'Insert a field (e.g. date, page number, TOC) at the current selection or end of document (requires WordApi 1.5+)',
        inputSchema: {
            type: 'object',
            properties: {
                field_type: {
                    type: 'string',
                    enum: [
                        'Date',
                        'Page',
                        'NumPages',
                        'FileName',
                        'Author',
                        'Title',
                        'TOC',
                        'Empty',
                    ],
                    description: 'The type of field to insert',
                },
                code: {
                    type: 'string',
                    description: 'Optional raw field code (e.g. "DATE \\\\@ \\"MMMM d, yyyy\\""). Overrides field_type if provided.',
                },
                position: {
                    type: 'string',
                    enum: ['selection', 'end'],
                    description: 'Where to insert (default: "selection")',
                },
            },
        },
    },
    execute: async (args) => {
        if (!isApiSupported('1.5'))
            return unsupportedApi('Fields', '1.5');
        const fieldType = args.field_type;
        const code = args.code;
        const position = args.position ?? 'selection';
        const fieldTypeMap = {
            Date: 31, // Word.FieldType.date
            Page: 33, // Word.FieldType.page
            NumPages: 26, // Word.FieldType.numPages
            FileName: 29, // Word.FieldType.fileName
            Author: 17, // Word.FieldType.author
            Title: 15, // Word.FieldType.title
            TOC: 13, // Word.FieldType.toc
            Empty: 1, // Word.FieldType.empty
        };
        const resolvedFieldType = fieldTypeMap[fieldType ?? 'Empty'] ?? fieldTypeMap['Empty'];
        const fieldText = code ?? '';
        await Word.run(async (context) => {
            const range = position === 'end'
                ? context.document.body.getRange(Word.RangeLocation.end)
                : context.document.getSelection();
            range.insertField(Word.InsertLocation.start, resolvedFieldType, fieldText, false);
            await context.sync();
        });
        return {
            content: [
                {
                    type: 'text',
                    text: `Field "${fieldType ?? 'Empty'}${code ? ` (${code})` : ''}" inserted at ${position}`,
                },
            ],
        };
    },
};
// ─── Lower Priority: Delete Paragraph ────────────────────────────────────────
const wordDeleteParagraph = {
    tool: {
        name: 'word_delete_paragraph',
        description: 'Delete a paragraph by its 0-based index',
        inputSchema: {
            type: 'object',
            properties: {
                paragraph_index: {
                    type: 'number',
                    description: 'The 0-based index of the paragraph to delete',
                },
            },
            required: ['paragraph_index'],
        },
    },
    execute: async (args) => {
        const paragraphIndex = args.paragraph_index;
        await Word.run(async (context) => {
            const paragraphs = context.document.body.paragraphs;
            paragraphs.load('items');
            await context.sync();
            if (paragraphIndex < 0 || paragraphIndex >= paragraphs.items.length) {
                throw new Error(`Paragraph index ${paragraphIndex} out of range (${paragraphs.items.length} paragraphs)`);
            }
            paragraphs.items[paragraphIndex].delete();
            await context.sync();
        });
        return {
            content: [{ type: 'text', text: `Paragraph ${paragraphIndex} deleted` }],
        };
    },
};
// ─── Lower Priority: Custom XML Parts ────────────────────────────────────────
const wordGetCustomXml = {
    tool: {
        name: 'word_get_custom_xml',
        description: 'Get all custom XML parts stored in the document (requires WordApi 1.4+)',
        inputSchema: {
            type: 'object',
            properties: {},
        },
    },
    execute: async () => {
        if (!isApiSupported('1.4'))
            return unsupportedApi('Custom XML Parts', '1.4');
        const result = await Word.run(async (context) => {
            const xmlParts = context.document.customXmlParts;
            xmlParts.load('items');
            await context.sync();
            const parts = [];
            for (const part of xmlParts.items) {
                const xml = part.getXml();
                await context.sync();
                parts.push({
                    id: part.id,
                    namespaceUri: part.namespaceUri,
                    xml: xml.value,
                });
            }
            return parts;
        });
        return { content: [{ type: 'text', text: JSON.stringify(result, null, 2) }] };
    },
};
const wordInsertCustomXml = {
    tool: {
        name: 'word_insert_custom_xml',
        description: 'Add a custom XML part to the document for storing structured metadata (requires WordApi 1.4+)',
        inputSchema: {
            type: 'object',
            properties: {
                xml: {
                    type: 'string',
                    description: 'The XML content to store',
                },
            },
            required: ['xml'],
        },
    },
    execute: async (args) => {
        if (!isApiSupported('1.4'))
            return unsupportedApi('Custom XML Parts', '1.4');
        const xml = args.xml;
        const partId = await Word.run(async (context) => {
            const part = context.document.customXmlParts.add(xml);
            part.load('id');
            await context.sync();
            return part.id;
        });
        return {
            content: [{ type: 'text', text: `Custom XML part added with id: ${partId}` }],
        };
    },
};
const wordDeleteCustomXml = {
    tool: {
        name: 'word_delete_custom_xml',
        description: 'Delete a custom XML part by its ID (requires WordApi 1.4+)',
        inputSchema: {
            type: 'object',
            properties: {
                id: {
                    type: 'string',
                    description: 'The ID of the custom XML part to delete',
                },
            },
            required: ['id'],
        },
    },
    execute: async (args) => {
        if (!isApiSupported('1.4'))
            return unsupportedApi('Custom XML Parts', '1.4');
        const id = args.id;
        await Word.run(async (context) => {
            const parts = context.document.customXmlParts;
            parts.load('items/id');
            await context.sync();
            const part = parts.items.find((p) => p.id === id);
            if (!part) {
                throw new Error(`Custom XML part with id "${id}" not found`);
            }
            part.delete();
            await context.sync();
        });
        return { content: [{ type: 'text', text: `Custom XML part ${id} deleted` }] };
    },
};
// ─── Export ──────────────────────────────────────────────────────────────────
export const wordTools = [
    // Tier 1: Core document operations
    wordGetDocumentText,
    wordSearchText,
    wordReplaceText,
    wordIsInDocument,
    wordInsertText,
    wordGetSelection,
    // Tier 2: Structural & formatting
    wordGetParagraphs,
    wordInsertParagraph,
    wordInsertTable,
    wordFormatText,
    // High Priority: Images
    wordInsertImage,
    // High Priority: Content Controls
    wordGetContentControls,
    wordInsertContentControl,
    wordSetContentControl,
    wordDeleteContentControl,
    // High Priority: Hyperlinks
    wordInsertHyperlink,
    // High Priority: Document Properties
    wordGetDocumentProperties,
    wordSetDocumentProperties,
    // High Priority: Comments (1.4+)
    wordGetComments,
    wordInsertComment,
    wordDeleteComment,
    // High Priority: Headers & Footers
    wordGetHeadersFooters,
    wordSetHeaderFooter,
    // Medium Priority: Sections
    wordGetSections,
    // Medium Priority: Breaks
    wordInsertBreak,
    // Medium Priority: Lists
    wordGetListInfo,
    wordSetListProperties,
    // Medium Priority: OOXML
    wordInsertOoxml,
    wordGetOoxml,
    // Medium Priority: Footnotes (1.5+)
    wordInsertFootnote,
    wordGetFootnotes,
    // Medium Priority: Styles (1.5+)
    wordGetStyles,
    // Lower Priority: Bookmarks (1.4+)
    wordGetBookmarks,
    wordInsertBookmark,
    wordDeleteBookmark,
    // Lower Priority: Tracked Changes
    wordGetTrackedChanges,
    wordAcceptTrackedChange,
    wordRejectTrackedChange,
    // Lower Priority: Save
    wordSaveDocument,
    // Lower Priority: Fields (1.5+)
    wordInsertField,
    // Lower Priority: Delete Paragraph
    wordDeleteParagraph,
    // Lower Priority: Custom XML
    wordGetCustomXml,
    wordInsertCustomXml,
    wordDeleteCustomXml,
];
